import React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AlertOctagon } from "lucide-react";
import { Button } from "components/button";

interface ErrorPageProps {
  title: string;
  message: string;
  backLink?: string;
  onRetry?: () => void;
  /** Offer the way back as well as the retry. Without a retry the way back is
   *  always offered, since it is then the only thing the page can do. */
  offerBack?: boolean;
  error?: Error | string;
}

export const ErrorPage = ({
  title,
  message,
  backLink = "/",
  error,
  onRetry,
  offerBack = false,
}: ErrorPageProps) => {
  const { t } = useTranslation(["error"]);
  const detail = error instanceof Error ? error.message : error;

  return (
    <div className="flex flex-col space-y-10 h-[75vh] flex-1 items-center justify-center text-center max-w-xl mx-auto container">
      <div className="rounded-full bg-icon-error-ring p-3">
        <div className="rounded-full bg-icon-error-background p-2">
          <AlertOctagon className="text-system-error h-8 w-8" />
        </div>
      </div>

      <div className="space-y-2">
        <h1 className="font-semibold text-2xl">{title}</h1>
        <p className="text-muted-foreground font-light whitespace-pre-line">
          {message}
        </p>
      </div>

      {/* Folded away under the sentence, not printed beneath it (Jira
          NAV-169). An SDK's own message is a wall of text that names the SDK and
          not the cause, and it drowns the sentence the reader needs. It stays
          one click away, because a tester who can paste it saves a round trip. */}
      {detail && (
        <details
          className="w-full text-left text-sm text-muted-foreground"
          data-testid="error-details"
        >
          <summary className="cursor-pointer text-center">
            {t("error:details")}
          </summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs">
            {detail}
          </pre>
        </details>
      )}

      {/* No support action: it linked to the app's own root in a new tab under
          a label inviting the reader to use a contact form the app does not
          have. Offering to reload or to go back is the whole of what this page
          can honestly do. */}
      <div className="grid grid-flow-col gap-3">
        {onRetry && (
          <Button
            label={t("error:actions.reload")}
            variant="outline"
            onClick={onRetry}
            className="text-sm"
          />
        )}
        {(!onRetry || offerBack) && (
          <Link to={backLink}>
            <Button
              label={t("error:actions.back")}
              variant="outline"
              className="text-sm font-normal"
            />
          </Link>
        )}
      </div>
    </div>
  );
};
